export class DataFrame {
    constructor(columns, rows) {
        this.columns = [];
        this.rows = [];
        this.index_column = null;
        this.columns = columns;
        this.rows = rows;
        this.index_column = this.columns.find((col) => col.id)?.name || null;
    }
    clone() {
        const newColumns = this.columns.map((col) => ({ ...col }));
        const newRows = this.rows.map((row) => ({ ...row }));
        const newDataFrame = new DataFrame(newColumns, newRows);
        newDataFrame.index_column = this.index_column;
        return newDataFrame;
    }
    renameColumns(renameDict) {
        Object.keys(renameDict).forEach((oldName) => {
            const newName = renameDict[oldName];
            if (!newName || oldName === newName)
                return;
            const column = this.columns.find((col) => col.name === oldName);
            if (column) {
                column.name = newName;
            }
            this.rows.forEach((row) => {
                if (oldName in row) {
                    row[newName] = row[oldName];
                    delete row[oldName];
                }
            });
        });
    }
    selectRowIndex(index) {
        if (index === null || this.index_column === null)
            return null;
        return this.rows.find((r) => r[this.index_column] === index) || null;
    }
    selectRowsIndex(index) {
        if (this.index_column === null)
            return [];
        return this.rows.filter((r) => index.includes(r[this.index_column]));
    }
    search(query) {
        const q = query.toLowerCase();
        const filteredRows = this.rows.filter((row) => this.columns
            .filter((col) => !col.id)
            .some((col) => String(row[col.name] ?? "").toLowerCase().includes(q)));
        return new DataFrame(this.columns, filteredRows);
    }
    filter(callback) {
        return new DataFrame(this.columns, this.rows.filter(callback));
    }
    sort(columnName, ascending = true) {
        if (!this.columns.some((c) => c.name === columnName)) {
            throw new Error(`Column ${columnName} does not exist`);
        }
        const sortedRows = [...this.rows].sort((a, b) => {
            if (a[columnName] < b[columnName])
                return ascending ? -1 : 1;
            if (a[columnName] > b[columnName])
                return ascending ? 1 : -1;
            return 0;
        });
        return new DataFrame(this.columns, sortedRows);
    }
    paginate(page, pageSize) {
        const start = (page - 1) * pageSize;
        const end = start + pageSize;
        return new DataFrame(this.columns, this.rows.slice(start, end));
    }
    toArray() {
        return this.rows;
    }
    rowCount() {
        return this.rows.length;
    }
    compare(other) {
        if (this.index_column === null || other.index_column === null) {
            throw new Error("Both DataFrames must have an index column for comparison.");
        }
        // Compare columns
        const addedColumns = this.columns.filter((col) => !other.columns.some((otherCol) => otherCol.name === col.name));
        const removedColumns = other.columns.filter((col) => !this.columns.some((thisCol) => thisCol.name === col.name));
        const unchangedColumns = this.columns.filter((col) => other.columns.some((otherCol) => otherCol.name === col.name &&
            otherCol.type === col.type &&
            otherCol.alias === col.alias &&
            otherCol.id === col.id));
        const modifiedColumns = this.columns.filter((col) => other.columns.some((otherCol) => otherCol.name === col.name) &&
            !unchangedColumns.includes(col));
        // Create maps for fast row comparison
        const thisRowMap = new Map(this.rows.map((row) => [row[this.index_column], row]));
        const otherRowMap = new Map(other.rows.map((row) => [row[other.index_column], row]));
        // Compare rows
        const addedRows = this.rows.filter((row) => !otherRowMap.has(row[this.index_column]));
        const removedRows = other.rows.filter((row) => !thisRowMap.has(row[other.index_column]));
        const unchangedRows = this.rows.filter((row) => {
            const otherRow = otherRowMap.get(row[this.index_column]);
            return (otherRow &&
                this.columns.every((col) => row[col.name] === otherRow[col.name]));
        });
        const modifiedRows = this.rows.filter((row) => {
            const otherRow = otherRowMap.get(row[this.index_column]);
            return (otherRow &&
                !unchangedRows.includes(row));
        });
        return {
            addedRows: new DataFrame(this.columns, addedRows),
            removedRows: new DataFrame(other.columns, removedRows),
            modifiedRows: new DataFrame(this.columns, modifiedRows),
            unchangedRows: new DataFrame(this.columns, unchangedRows),
            addedColumns,
            removedColumns,
            modifiedColumns,
            unchangedColumns
        };
    }
    getFirstRows(n = 5) {
        return new DataFrame(this.columns, this.rows.slice(0, n));
    }
    changeType(columnName, newType) {
        const column = this.columns.find(col => col.name === columnName);
        if (!column) {
            throw new Error(`Column ${columnName} does not exist`);
        }
        this.rows = this.rows.map(row => {
            let newValue;
            const value = row[columnName];
            switch (newType) {
                case 'string':
                    newValue = String(value);
                    break;
                case 'number':
                    newValue = Number(value);
                    break;
                case 'boolean':
                    newValue = Boolean(value);
                    break;
                default:
                    newValue = value;
            }
            return {
                ...row,
                [columnName]: newValue
            };
        });
        column.type = newType;
    }
    deleteColumn(columnName) {
        this.columns = this.columns.filter(col => col.name !== columnName);
        this.rows = this.rows.map(row => {
            const newRow = { ...row };
            delete newRow[columnName];
            return newRow;
        });
        if (this.index_column === columnName) {
            this.index_column = null;
        }
    }
}
//# sourceMappingURL=DataFrame.js.map